import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { mergeAttributes } from '@tiptap/core'
import Mention from '@tiptap/extension-mention'
import { ReactRenderer } from '@tiptap/react'
import tippy, { Instance } from 'tippy.js'

import { SuggestionOptions } from '@tiptap/suggestion'
import { PluginKey } from '@tiptap/pm/state'
import { useAppContext } from 'src/lib/appContext'

// Add interfaces for UserMentionList props and items
interface UserMentionListProps {
  items: Array<{
    id: string;
    name: string;
    type: 'user';
  }>;
  command: (props: { id: string; label: string }) => void;
  onKeyDown?: (props: KeyDownProps) => boolean;
}

interface KeyDownProps {
  event: KeyboardEvent;
}

// UserMentionList component
const UserMentionList = forwardRef<UserMentionListRef, UserMentionListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  useEffect(() => setSelectedIndex(0), [items])

  const selectItem = (index: number) => {
    const item = items[index]
    if (item) {
      command({ id: item.id, label: item.name })
    }
  }

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: KeyDownProps) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length)
        return true
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % items.length)
        return true
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex)
        return true
      }

      return false
    },
  }))

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-1 max-h-80 overflow-y-auto text-xs">
      {items.map((item, index: number) => (
        <button
          className={`w-full text-left px-3 py-2 rounded-md ${
            index === selectedIndex
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-100'
          }`}
          key={item.id}
          onClick={() => selectItem(index)}
        >
          @{item.name}
        </button>
      ))}
    </div>
  )
})

// Update the ReactRenderer type
interface UserMentionListRef {
  onKeyDown: (props: KeyDownProps) => boolean;
}

// Update getSuggestionHandler with proper types
const getSuggestionHandler = (
  organizationMembers: Array<{ user: { id: string; name: string } }>
): Omit<SuggestionOptions, 'editor'> => {
  return {
    items: ({ query }) => {
      const users = organizationMembers.map(member => ({
        id: member.user.id,
        name: member.user.name,
        type: 'user' as const
      }))

      return users
        .filter(user => {
          const searchStr = user.name.toLowerCase()
          return searchStr.includes(query.toLowerCase())
        })
        .slice(0, 5)
    },
    render: () => {
      let component: ReactRenderer
      let popup: Instance<any>[] | null = null

      return {
        onStart: (props) => {
          component = new ReactRenderer(UserMentionList, {
            props: {
              ...props,
            },
            editor: props.editor,
          })

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          })
        },

        onUpdate(props) {
          component.updateProps(props)

          if (popup?.[0]) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            })
          }
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide()
            return true
          }

          return component.ref?.onKeyDown ? component.ref.onKeyDown(props) : false
        },

        onExit() {
          popup?.[0]?.destroy()
          component.destroy()
        },
      }
    },
  }
}

export interface UserMentionPluginProps {
  editable?: boolean
}

export const useUserMentionPlugin = (config?: UserMentionPluginProps) => {
  const { currentOrg } = useAppContext()
  const organizationMembers = currentOrg?.members || []

  const plugin = Mention.extend({
    name: 'userMention',
  }).configure({
    HTMLAttributes: {
      class: 'inline-flex items-center font-medium text-blue-700 no-underline',
    },
    renderHTML({ options, node }) {
      // Find the user by ID
      const user = organizationMembers.find(member => member.user.id === node.attrs.id)
      const tooltip = `${user?.user.name || 'Unknown User'}`

      if (config?.editable) {
        return [
          'span',
          mergeAttributes({
            'data-tooltip': tooltip,
          }, options.HTMLAttributes),
          `@${node.attrs.label}`
        ]
      }

      // For non-editable view, just display the mention without a link
      return [
        'span',
        mergeAttributes({
          'data-tooltip': tooltip,
        }, options.HTMLAttributes),
        `@${node.attrs.label}`
      ]
    },
    suggestion: {
      ...getSuggestionHandler(organizationMembers),
      char: '@',
      pluginKey: new PluginKey('userMention'),
    }
  })

  return { plugin }
}